import React from 'react';
import ActivityForm from './ActivityForm';
import './Calendar.css';
import DailyAgenda from './DailyAgenda';
import { useTranslation } from 'react-i18next';

const Calendar = ({ onLogout }) => {
    const { t } = useTranslation();
    const [currentDate, setCurrentDate] = React.useState(new Date());
    const [selectedDate, setSelectedDate] = React.useState(null);
    const [showActivityForm, setShowActivityForm] = React.useState(false);
    const [showCalendar, setShowCalendar] = React.useState(false); // New state for calendar visibility

    // Load selected date from localStorage on mount
    React.useEffect(() => {
        const savedDate = localStorage.getItem('selectedCalendarDate');
        if (savedDate) {
            const date = new Date(savedDate);
            setSelectedDate(date);
            setCurrentDate(date); // Also set current month to the saved date's month
        }
    }, []);

    // Save selected date to localStorage whenever it changes
    React.useEffect(() => {
        if (selectedDate) {
            localStorage.setItem('selectedCalendarDate', selectedDate.toISOString());
        } else {
            localStorage.removeItem('selectedCalendarDate');
        }
    }, [selectedDate]);

    const handlePreviousMonth = () => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() - 1);
        setCurrentDate(newDate);
        setSelectedDate(null); // Clear selected date when changing month
    };

    const handleNextMonth = () => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + 1);
        setCurrentDate(newDate);
        setSelectedDate(null); // Clear selected date when changing month
    };

    const handleDayClick = (day) => {
        const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (clickedDate >= today) {
            setSelectedDate(clickedDate);
            setShowCalendar(false); // Collapse calendar after date selection
        } else {
            alert(t('cannot_select_past_dates'));
        }
    };

    const handleActivityAdded = () => {
        setShowActivityForm(false); // Hide form
        // Refresh activities for the currently selected date, or current month if no date selected
        if (selectedDate) {
            // No need to fetch activities here, DailyAgenda will handle it
        } else {
            // No need to fetch activities here, DailyAgenda will handle it
        }
    };

    const handleCancelActivityForm = () => {
        setShowActivityForm(false); // Hide form
    };

    // Calendar Grid Logic (same as before)
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
        days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isPast = date < today;
        const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

        days.push(
            <div
                key={day}
                className={`calendar-day ${isPast ? 'past' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleDayClick(day)}
                style={{ cursor: isPast ? 'not-allowed' : 'pointer' }}
            >
                {day}
            </div>
        );
    }

    return (
        <div className="calendar-container">
            {/* error state is now in DailyAgenda */}
            {!showCalendar ? (
                <button onClick={() => setShowCalendar(true)} className="calendar-icon-button">
                    📅
                </button>
            ) : (
                <>
                    <h2>{t('calendar_page_title')}</h2>
                    <div className="calendar-header">
                        <button onClick={handlePreviousMonth}>◀</button>
                        <h3>{currentDate.toLocaleString('default', { month: 'long', 'year': 'numeric' })}</h3>
                        <button onClick={handleNextMonth}>▶</button>
                        
                        <button onClick={() => setShowCalendar(false)} style={{ marginLeft: '10px' }}>✖</button>
                    </div>
                    <div className="calendar-grid">
                        <div className="calendar-day-name">{t('days_short.sun')}</div>
                        <div className="calendar-day-name">{t('days_short.mon')}</div>
                        <div className="calendar-day-name">{t('days_short.tue')}</div>
                        <div className="calendar-day-name">{t('days_short.wed')}</div>
                        <div className="calendar-day-name">{t('days_short.thu')}</div>
                        <div className="calendar-day-name">{t('days_short.fri')}</div>
                        <div className="calendar-day-name">{t('days_short.sat')}</div>
                        {days}
                    </div>

                    <button onClick={() => setShowActivityForm(true)} style={{ marginTop: '20px' }}>{t('add_new_activity_btn')}</button>

                    {showActivityForm && (
                        <ActivityForm onActivityAdded={handleActivityAdded} onCancel={handleCancelActivityForm} />
                    )}
                </>
            )}
            {/* Activities display moved to DailyAgenda */}
            <DailyAgenda selectedDate={selectedDate} onLogout={onLogout} />
        </div>
    );
};

export default Calendar;
